import React, { useState, useEffect, useRef, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Tv, RefreshCw, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api, BACKEND_URL } from '@/api';
import { useViewerAuth } from '@/contexts/ViewerAuthContext';

const ACTIVATE_URL = typeof window !== 'undefined'
  ? `${window.location.origin}/activate`
  : '/activate';

const TV_TOKEN_KEY = 'nls_tv_token';

export const TVSignIn = ({ onSignedIn }) => {
  const [code, setCode] = useState(null);
  const [deviceToken, setDeviceToken] = useState(null);
  const [status, setStatus] = useState('loading'); // loading, pending, activated, expired, error, remembered
  const [expiresIn, setExpiresIn] = useState(900);
  const [rememberedViewer, setRememberedViewer] = useState(null);
  const pollRef = useRef(null);
  const countdownRef = useRef(null);
  const { setViewerDirect: setViewer } = useViewerAuth();

  // Check for a remembered TV token on mount
  useEffect(() => {
    const storedToken = localStorage.getItem(TV_TOKEN_KEY);
    if (!storedToken) return;

    const verify = async () => {
      try {
        const res = await api.tvVerify(storedToken);
        if (res.data.valid && res.data.viewer) {
          setRememberedViewer(res.data.viewer);
          setStatus('remembered');
        } else {
          localStorage.removeItem(TV_TOKEN_KEY);
        }
      } catch {
        localStorage.removeItem(TV_TOKEN_KEY);
      }
    };
    verify();
  }, []);

  const useRememberedToken = useCallback(() => {
    const storedToken = localStorage.getItem(TV_TOKEN_KEY);
    if (storedToken && rememberedViewer) {
      localStorage.setItem('viewer_token', storedToken);
      setViewer(rememberedViewer);
      setStatus('activated');
      if (onSignedIn) onSignedIn(storedToken, rememberedViewer);
    }
  }, [rememberedViewer, setViewer, onSignedIn]);

  const forgetTV = useCallback(() => {
    localStorage.removeItem(TV_TOKEN_KEY);
    setRememberedViewer(null);
    setStatus('loading');
  }, []);

  const requestCode = useCallback(async () => {
    setStatus('loading');
    try {
      const res = await api.tvRequestCode();
      setCode(res.data.code);
      setDeviceToken(res.data.device_token);
      setExpiresIn(res.data.expires_in || 900);
      setStatus('pending');
    } catch {
      setStatus('error');
    }
  }, []);

  // Request code on mount (only if no remembered token) or after forgetting
  useEffect(() => {
    if (status === 'loading' && !rememberedViewer) {
      const storedToken = localStorage.getItem(TV_TOKEN_KEY);
      if (!storedToken) {
        requestCode();
      }
    }
  }, [requestCode, status, rememberedViewer]);

  // Poll for activation
  useEffect(() => {
    if (status !== 'pending' || !deviceToken) return;

    const poll = async () => {
      try {
        const res = await api.tvPoll(deviceToken);
        if (res.data.status === 'activated' && res.data.token) {
          setStatus('activated');
          // Store the token for this session AND remember the TV
          localStorage.setItem('viewer_token', res.data.token);
          localStorage.setItem(TV_TOKEN_KEY, res.data.token);
          if (res.data.viewer) {
            setViewer(res.data.viewer);
          }
          if (onSignedIn) onSignedIn(res.data.token, res.data.viewer);
        } else if (res.data.status === 'expired') {
          setStatus('expired');
        }
      } catch {
        // Silent — keep polling
      }
    };

    pollRef.current = setInterval(poll, 3000);
    return () => clearInterval(pollRef.current);
  }, [status, deviceToken, setViewer, onSignedIn]);

  // Countdown timer
  useEffect(() => {
    if (status !== 'pending') return;
    countdownRef.current = setInterval(() => {
      setExpiresIn(prev => {
        if (prev <= 1) {
          setStatus('expired');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(countdownRef.current);
  }, [status]);

  const formatCountdown = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const qrUrl = code ? `${ACTIVATE_URL}?code=${code}` : '';

  // ============== REMEMBERED STATE ==============
  if (status === 'remembered' && rememberedViewer) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center" data-testid="tv-signin-remembered">
        <div className="w-14 h-14 bg-sky-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Tv className="w-7 h-7 text-sky-400" />
        </div>
        <h3 className="text-lg font-bold text-white mb-1">Welcome Back</h3>
        <p className="text-gray-400 text-sm mb-1">This TV is remembered as</p>
        <p className="text-white font-semibold mb-6">{rememberedViewer.name || rememberedViewer.email}</p>
        <div className="space-y-2 w-full max-w-xs">
          <Button
            onClick={useRememberedToken}
            className="w-full bg-sky-500 hover:bg-sky-600 text-white font-bold"
            data-testid="tv-continue-btn"
          >
            Continue Watching
          </Button>
          <button
            onClick={forgetTV}
            className="w-full text-gray-500 hover:text-gray-300 text-sm py-2 transition-colors"
            data-testid="tv-forget-btn"
          >
            Use a different account
          </button>
        </div>
      </div>
    );
  }

  // ============== ACTIVATED STATE ==============
  if (status === 'activated') {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center" data-testid="tv-signin-activated">
        <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
        <h3 className="text-xl font-bold text-white mb-2">Signed In!</h3>
        <p className="text-gray-400">Your TV is now connected to your account.</p>
      </div>
    );
  }

  // ============== EXPIRED STATE ==============
  if (status === 'expired') {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center" data-testid="tv-signin-expired">
        <Tv className="w-12 h-12 text-gray-600 mb-4" />
        <h3 className="text-lg font-bold text-white mb-2">Code Expired</h3>
        <p className="text-gray-400 text-sm mb-4">Request a new code to sign in.</p>
        <Button onClick={requestCode} className="bg-sky-500 hover:bg-sky-600">
          <RefreshCw className="w-4 h-4 mr-2" /> Get New Code
        </Button>
      </div>
    );
  }

  // ============== LOADING STATE ==============
  if (status === 'loading' || !code) {
    return (
      <div className="flex flex-col items-center justify-center p-8" data-testid="tv-signin-loading">
        <Loader2 className="w-10 h-10 text-sky-400 animate-spin mb-3" />
        <p className="text-gray-400 text-sm">Generating sign-in code...</p>
      </div>
    );
  }

  // ============== PENDING STATE (Main QR + Code Display) ==============
  return (
    <div className="flex flex-col items-center justify-center p-6 text-center" data-testid="tv-signin-pending">
      <h3 className="text-lg font-bold text-white mb-1">Sign In on Your TV</h3>
      <p className="text-gray-400 text-sm mb-6 max-w-xs">
        Scan the QR code with your phone, or go to <span className="text-sky-400 font-mono">{ACTIVATE_URL.replace('https://', '')}</span>
      </p>

      {/* QR Code */}
      <div className="bg-white p-4 rounded-2xl mb-6 shadow-lg" data-testid="tv-qr-code">
        <QRCodeSVG
          value={qrUrl}
          size={180}
          bgColor="#ffffff"
          fgColor="#0c0a09"
          level="M"
          includeMargin={false}
        />
      </div>

      {/* Activation Code */}
      <div className="mb-4">
        <p className="text-gray-500 text-xs uppercase tracking-widest mb-2">Enter this code</p>
        <div className="flex items-center justify-center gap-1.5" data-testid="tv-activation-code">
          {code.split('').map((char, i) => (
            <span
              key={i}
              className="w-11 h-14 bg-gray-800 border-2 border-gray-700 rounded-lg flex items-center justify-center text-2xl font-bold text-white tracking-widest"
            >
              {char}
            </span>
          ))}
        </div>
      </div>

      {/* Timer */}
      <p className="text-gray-600 text-xs">
        Code expires in <span className="text-gray-400 font-mono">{formatCountdown(expiresIn)}</span>
      </p>
    </div>
  );
};
